import { add_cart_ga, remove_from_cart_ga } from "../../utilities/analytics";
import { getCurrency } from "../../utilities/misc";
import * as actionTypes from "../actionTypes";

export type service_types = "typical" | "extra" | "gift";
interface service_type {
  type: service_types;
}
export type gift_value = {
  service_id: number;
  quantity: number;
  value: number;
};

export interface cartItem extends service_type {
  id: number;
  item: actionTypes.service & { extra: false; parent_id: undefined };
  quantity: number;
  parent_id: undefined;
  type: "typical" | "gift";
}

export interface extrasCartItem extends service_type {
  id: number;
  quantity: number;
  item: actionTypes.extra_service_ext;
  parent_id: number;
  type: "extra";
}

export const initialState: {
  cart: cartItem[];
  extras: extrasCartItem[];
} = {
  cart: [],
  extras: []
};

export type cartInitialState = typeof initialState;
export type cartInitKeys = keyof typeof initialState;
export type Item = cartInitialState[cartInitKeys][number];

const resolve = (num: number) => {
  if (num < 0) {
    return 0;
  } else {
    return num;
  }
};

const removeItem = (
  payload: { id: number },
  state: cartInitialState,
  key: cartInitKeys
): Item[] => {
  const newCart = (state[key] as Item[]).filter(
    (item) => payload!.id !== item.id
  );
  const item = (state[key] as Item[]).find((item) => {
    return item.id === payload?.id;
  });

  if (item) {
    remove_from_cart_ga({
      currency: getCurrency(),
      items: [item],
      value: item.quantity * item.item.price_with_discount
    });
  }

  return newCart;
};

function getTotal(items: Item[]): number {
  let total = 0;

  items.forEach((item) => {
    total = total + item.quantity * item.item.price_with_discount;
  });

  return total;
}

const getKey = (type?: service_types): cartInitKeys => {
  if (type === "extra") {
    return "extras";
  } else if (type === "typical") {
    return "cart";
  } else {
    return "cart";
  }
};

const reducer = (
  state = initialState,
  action:
    | {
        type: typeof actionTypes.ADD_TO_CART;
        payload: Item;
      }
    | { type: typeof actionTypes.CLEAR_CART; payload?: boolean }
    | {
        type: typeof actionTypes.REMOVE_FROM_CART;
        payload: { id: number; type: service_types };
      }
    | { type: typeof actionTypes.REMOVE_EXTRAS_FROM_CART }
    | { type: typeof actionTypes.CHANGE_CART_ITEM; payload: Item }
    | { type: typeof actionTypes.SET_ENTIRE_CART; payload: cartInitialState }
): cartInitialState => {
  switch (action.type) {
    case actionTypes.ADD_TO_CART: {
      const key = getKey(action.payload.type);
      const itemExists = state[key].some((item) => {
        return action.payload?.id === item.id;
      });

      if (!itemExists && action.payload) {
        const items = [action.payload!];

        add_cart_ga({
          currency: getCurrency(),
          value: getTotal(items),
          items
        });
      }

      return {
        ...state,
        [key]: [action.payload, ...state[key]]
      };
    }
    case actionTypes.CLEAR_CART: {
      const ignoreAnalytics = action.payload;
      const ga_cart = state.cart;
      const ga_extras = state.extras;

      if (!ignoreAnalytics) {
        remove_from_cart_ga({
          currency: getCurrency(),
          items: [...ga_cart, ...ga_extras],
          value: getTotal([...ga_cart, ...ga_extras])
        });
      }

      return {
        ...state,
        cart: [],
        extras: []
      };
    }
    case actionTypes.REMOVE_FROM_CART: {
      const key = getKey(action.payload.type);
      const newCart = removeItem(action.payload, state, key);

      return {
        ...state,
        [key]: [...newCart]
      };
    }
    case actionTypes.REMOVE_EXTRAS_FROM_CART: {
      const ga_extras = state.extras;

      remove_from_cart_ga({
        currency: getCurrency(),
        items: ga_extras,
        value: getTotal(ga_extras)
      });

      return {
        ...state,
        extras: []
      };
    }
    case actionTypes.CHANGE_CART_ITEM: {
      const key = getKey(action.payload.type);
      if (resolve(action.payload.quantity)) {
        let found = false;
        const newCart = state[key].map((item) => {
          if (action.payload.id === item.id) {
            found = true;
            return {
              ...item,
              ...action.payload
            };
          } else {
            return item;
          }
        });
        if (!found && action.payload) {
          newCart.push(action.payload);

          const items = [action.payload];

          add_cart_ga({
            currency: getCurrency(),
            value: getTotal(items),
            items
          });
        }
        return {
          ...state,
          [key]: [...newCart]
        };
      } else {
        // remove item from cart if the item's quantity is zero
        const newCart = removeItem(action.payload, state, key);

        return {
          ...state,
          [key]: [...newCart]
        };
      }
    }

    case actionTypes.SET_ENTIRE_CART: {
      return {
        ...state,
        ...action.payload
      };
    }
    default: {
      return state;
    }
  }
};

export type card_action_params = Parameters<typeof reducer>[1];

export default reducer;
